import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import {
  IVA,
  fmtMXN,
  fmtPct,
  limitesMonto,
  r2,
  type ParamsCredito,
  type Solicitud,
} from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";
import { ParametrosCredito } from "./ParametrosCredito";
import { CargosSolicitud } from "./CargosSolicitud";

const TIPOS_GARANTIA = [
  "Prenda sobre maquinaria",
  "Garantía vehicular",
  "Hipotecaria",
  "Prenda sobre inventario",
  "Aval solidario",
];

function Regla({ ok, texto }: { ok: boolean; texto: string }) {
  return (
    <p className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald" : "text-destructive"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {texto}
    </p>
  );
}

export function SolicitudCredito() {
  const {
    productos,
    producto,
    solicitud,
    setSolicitud,
    condiciones,
    valorUdi,
    cambiarProductoSolicitud,
  } = useOperativo();
  const set = (patch: Partial<Solicitud>) => setSolicitud({ ...solicitud, ...patch });
  const setP = (patch: Partial<ParamsCredito>) =>
    set({ params: { ...solicitud.params, ...patch } });
  const p = solicitud.params;

  const lim = limitesMonto(producto, valorUdi);
  const montoOk = solicitud.monto >= lim.min && solicitud.monto <= lim.max;
  const moraSugerida = r2(p.tasaAnual * 2);
  const plazoOk =
    Number.isInteger(solicitud.plazo) &&
    solicitud.plazo >= producto.plazoMin &&
    solicitud.plazo <= producto.plazoMax;
  const diaOk =
    Number.isInteger(solicitud.diaPago) && solicitud.diaPago >= 1 && solicitud.diaPago <= 31;
  const fechaOk = !!solicitud.fechaDesembolso;
  const graciaOk = p.periodosGracia >= 0 && p.periodosGracia < (solicitud.plazo || 1);
  const avalOk = !p.requiereAval || solicitud.avalNombre.trim().length > 2;

  const g = solicitud.garantia;
  const aforo = solicitud.monto > 0 && g.valor > 0 ? g.valor / solicitud.monto : 0;
  const antiguedad =
    g.fechaFactura && solicitud.fechaDesembolso
      ? (new Date(solicitud.fechaDesembolso).getTime() - new Date(g.fechaFactura).getTime()) /
        (365.25 * 86400000)
      : 0;
  const aforoOk = aforo >= producto.aforoMin;
  const antigOk = antiguedad >= 0 && antiguedad <= producto.antiguedadMaxAnios;
  const garantiaOk =
    !producto.requiereGarantia || (aforoOk && antigOk && !!g.descripcion && g.valor > 0);
  const valido = montoOk && plazoOk && diaOk && fechaOk && garantiaOk && graciaOk && avalOk;

  const per = condiciones.etiquetaPeriodo;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
          Solicitud y configuración de la simulación
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Los parámetros se heredan del producto seleccionado y pueden ajustarse crédito por
          crédito. Tasa del período = tasa anual / número de pagos por año · RN-05 comisión circular
          resuelta algebraicamente.
        </p>
      </div>

      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-navy" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Parámetros variables del crédito
            </h3>
          </div>
          <div className="w-full max-w-xs space-y-1.5">
            <Label>Producto (hereda los defaults)</Label>
            <Select value={solicitud.productoId} onValueChange={(v) => cambiarProductoSolicitud(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {productos
                  .filter((x) => !x.esRevolvente)
                  .map((x) => (
                    <SelectItem key={x.id} value={x.id}>
                      {x.nombre}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground num">
              {fmtMXN(lim.min)} – {fmtMXN(lim.max)} · {producto.plazoMin}–{producto.plazoMax} pagos
            </p>
            {(lim.minUdis || lim.maxUdis) && (
              <p className="text-xs text-muted-foreground num">
                {(lim.maxUdis ?? lim.minUdis)!.toLocaleString("es-MX")} UDIS ≈{" "}
                {fmtMXN(lim.maxUdis ? lim.max : lim.min)} al valor UDI actual ({valorUdi})
              </p>
            )}
            {producto.requiereGarantia && (
              <p className="text-xs text-muted-foreground">
                Garantía: {producto.tipoGarantia} · aforo mín. {producto.aforoMin}:1
                {producto.antiguedadMaxAnios < 50
                  ? ` · antigüedad máx. ${producto.antiguedadMaxAnios} años`
                  : ""}
              </p>
            )}
          </div>
        </div>

        <ParametrosCredito
          idPrefijo="sol"
          params={p}
          onChange={setP}
          plazo={{ valor: solicitud.plazo, onChange: (n) => set({ plazo: n }) }}
          monto={{ valor: solicitud.monto, onChange: (n) => set({ monto: n }) }}
        />

        {r2(p.tasaMoratoria) !== moraSugerida && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-navy-soft px-3 py-2">
            <p className="text-xs text-navy">
              La tasa moratoria ({fmtPct(p.tasaMoratoria, 0)}) no equivale a 2× la ordinaria (
              {fmtPct(moraSugerida, 0)}).
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setP({ tasaMoratoria: moraSugerida })}
            >
              Recalcular a 2×
            </Button>
          </div>
        )}

        <div className="mt-5 grid gap-4 border-t pt-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="op-fecha">Fecha de desembolso</Label>
            <Input
              id="op-fecha"
              type="date"
              value={solicitud.fechaDesembolso}
              onChange={(e) => set({ fechaDesembolso: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="op-dia">Día de pago (periodicidad mensual)</Label>
            <Input
              id="op-dia"
              type="number"
              inputMode="decimal"
              min={1}
              max={31}
              className="num"
              value={Number.isNaN(solicitud.diaPago) ? "" : solicitud.diaPago}
              onChange={(e) => set({ diaPago: e.target.valueAsNumber })}
            />
          </div>
          {p.requiereAval && (
            <div className="space-y-1.5">
              <Label htmlFor="op-aval">Aval / obligado solidario</Label>
              <Input
                id="op-aval"
                value={solicitud.avalNombre}
                placeholder="Nombre completo o razón social"
                onChange={(e) => set({ avalNombre: e.target.value.slice(0, 120) })}
              />
              <Regla
                ok={avalOk}
                texto={avalOk ? "Aval capturado" : "El aval es obligatorio para este producto"}
              />
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-1.5 border-t pt-4 sm:grid-cols-2 lg:grid-cols-3">
          <Regla
            ok={montoOk}
            texto={
              montoOk
                ? "Monto dentro del rango del producto"
                : `El monto debe estar entre ${fmtMXN(lim.min)} y ${fmtMXN(lim.max)}`
            }
          />
          <Regla
            ok={plazoOk}
            texto={
              plazoOk
                ? "Número de pagos permitido"
                : `El plazo debe estar entre ${producto.plazoMin} y ${producto.plazoMax}`
            }
          />
          <Regla
            ok={diaOk}
            texto={diaOk ? "Día de pago válido" : "El día debe ser un entero entre 1 y 31"}
          />
          <Regla
            ok={fechaOk}
            texto={fechaOk ? "Fecha de desembolso capturada" : "Captura la fecha de desembolso"}
          />
          <Regla
            ok={graciaOk}
            texto={
              graciaOk ? "Período de gracia válido" : "La gracia debe ser menor al número de pagos"
            }
          />
          <Regla
            ok={p.tipoCredito === "CRÉDITO SIMPLE"}
            texto={
              p.tipoCredito === "CRÉDITO SIMPLE"
                ? "Crédito simple: simulación completa"
                : "Revolvente: opéralo en la subsección “Línea Revolvente”"
            }
          />
        </div>
      </Card>

      <CargosSolicitud />

      {producto.requiereGarantia && (
        <Card className="print-block border-navy/15 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-emerald" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Garantía requerida
            </h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="g-desc">Descripción del bien</Label>
              <Input
                id="g-desc"
                value={g.descripcion}
                onChange={(e) =>
                  set({ garantia: { ...g, descripcion: e.target.value.slice(0, 160) } })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-valor">Valor del bien / avalúo</Label>
              <Input
                id="g-valor"
                type="number"
                inputMode="decimal"
                className="num"
                value={Number.isNaN(g.valor) ? "" : g.valor}
                onChange={(e) => set({ garantia: { ...g, valor: e.target.valueAsNumber } })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-fecha">Fecha de factura</Label>
              <Input
                id="g-fecha"
                type="date"
                value={g.fechaFactura}
                onChange={(e) => set({ garantia: { ...g, fechaFactura: e.target.value } })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de garantía</Label>
              <Select value={g.tipo} onValueChange={(v) => set({ garantia: { ...g, tipo: v } })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_GARANTIA.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 grid gap-2 rounded-md bg-surface px-4 py-3 sm:grid-cols-3">
            <p className="text-xs text-muted-foreground">
              Aforo calculado: <span className="num text-navy">{aforo.toFixed(2)}x</span> (mín.{" "}
              {producto.aforoMin}x)
            </p>
            <p className="text-xs text-muted-foreground">
              Antigüedad del bien:{" "}
              <span className="num text-navy">{antiguedad.toFixed(1)} años</span> (máx.{" "}
              {producto.antiguedadMaxAnios})
            </p>
            <div className="space-y-1">
              <Regla
                ok={aforoOk}
                texto={aforoOk ? "Aforo suficiente" : "Aforo insuficiente para el producto"}
              />
              <Regla
                ok={antigOk}
                texto={
                  antigOk ? "Antigüedad dentro del máximo" : "El bien excede la antigüedad máxima"
                }
              />
            </div>
          </div>
        </Card>
      )}

      {!valido && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Los resultados se muestran a título indicativo: hay validaciones pendientes en la
          solicitud.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="print-block border-navy/15 bg-navy p-5 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-primary-foreground/70">
            Cuota {per} {condiciones.periodosGracia > 0 ? "(post gracia)" : ""}
          </p>
          <p className="mt-2 num text-3xl font-semibold text-primary-foreground">
            {fmtMXN(condiciones.cuotaPeriodo)}
          </p>
          {p.moneda === "UDIS" && (
            <p className="mt-1 text-xs text-primary-foreground/70 num">
              ≈ {fmtMXN(condiciones.cuotaPeriodo * (p.valorUdi || 0))} MXN
            </p>
          )}
        </Card>
        <Card className="print-block border-emerald/30 bg-emerald/10 p-5 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald">Total a pagar</p>
          <p className="mt-2 num text-3xl font-semibold text-emerald">
            {fmtMXN(condiciones.totalEstimado)}
          </p>
          {p.moneda === "UDIS" && (
            <p className="mt-1 text-xs text-emerald num">
              ≈ {fmtMXN(condiciones.totalEstimado * (p.valorUdi || 0))} MXN
            </p>
          )}
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { l: "Monto financiado", v: fmtMXN(condiciones.montoFinanciado), t: "navy" },
          { l: "Monto neto desembolsado", v: fmtMXN(condiciones.montoNetoDesembolsado), t: "navy" },
          { l: "CAT sin IVA", v: fmtPct(condiciones.catSinIva), t: "chart3" },
          { l: "CAT con IVA", v: fmtPct(condiciones.catConIva), t: "chart3" },
          { l: `Tasa ${per}`, v: fmtPct(condiciones.tasaPeriodica * 100), t: "navy" },
          {
            l: "Tasa moratoria anual",
            v: fmtPct(condiciones.tasaMoraAnual * 100),
            t: "destructive",
          },
          {
            l:
              condiciones.penaCargos > 1
                ? `Pena convencional (c/IVA) (${condiciones.penaCargos} cargos)`
                : "Pena convencional (c/IVA)",
            v: fmtMXN(condiciones.penaConIva),
            t: "destructive",
          },
          { l: "Pagos por año", v: condiciones.pagosPorAnio.toFixed(2), t: "navy" },
        ].map((c) => (
          <Card key={c.l} className="print-block border-navy/15 p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {c.l}
            </p>
            <p
              className={`mt-2 num text-xl font-semibold ${
                c.t === "chart3"
                  ? "text-chart-3"
                  : c.t === "destructive"
                    ? "text-destructive"
                    : "text-navy"
              }`}
            >
              {c.v}
            </p>
          </Card>
        ))}
      </div>

      <Card className="print-block border-navy/15 bg-surface p-5 text-xs leading-relaxed text-muted-foreground shadow-sm">
        <p>
          Comisiones financiadas:{" "}
          <span className="num text-navy">{fmtMXN(condiciones.comisionesFinanciadas)}</span> (
          {fmtPct(condiciones.pctCircularConIva * 100)} circular con IVA) · cobros externos no
          financiados: <span className="num text-navy">{fmtMXN(condiciones.cobrosExternos)}</span>.
          IVA aplicado sobre intereses:{" "}
          {p.ivaEnCuota ? fmtPct(IVA * 100) : "no aplica (cuota con tasa pura)"}. Para simular sobre
          el monto solicitado exacto, marca la comisión de apertura como no financiada en el
          configurador.
        </p>
      </Card>
    </div>
  );
}
